import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { ChartBarIcon } from '@heroicons/react/24/outline';

const IssuesTrendChart = ({ dates = [], scores = [], loading = false, days = 30 }) => {
  // Calculate issues per week from the scores data
  // For simplicity, we'll group by weeks and count issues based on score changes
  const calculateWeeklyIssues = () => {
    if (!dates || dates.length === 0) return [];

    const weeklyData = [];
    let currentWeek = null;
    let weekIssues = 0;
    let weekStart = null;

    dates.forEach((date, index) => {
      const d = new Date(date);
      const weekNum = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));

      if (currentWeek !== weekNum) {
        if (currentWeek !== null) {
          weeklyData.push({
            week: weekStart,
            issues: weekIssues,
            label: new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          });
        }
        currentWeek = weekNum;
        weekStart = date;
        weekIssues = 0;
      }

      // Calculate issues based on health score drop (simplified)
      const healthDrop = index > 0 ? Math.max(0, scores[index - 1] - scores[index]) : 0;
      weekIssues += Math.round(healthDrop / 2); // Rough estimation
    });

    // Add last week
    if (weekStart) {
      weeklyData.push({
        week: weekStart,
        issues: weekIssues,
        label: new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }

    return weeklyData;
  };

  const weeklyData = calculateWeeklyIssues();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-900">Week of {data.label}</p>
          <p className="text-sm text-gray-600 mt-1">
            Issues: <span className="font-bold text-red-600">{data.issues}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-28 mb-6 animate-pulse"></div>

        {/* Bar Chart Skeleton */}
        <div className="h-64 relative">
          {/* Simulated bar chart */}
          <div className="absolute inset-0 flex items-end gap-3 px-4 pb-8">
            {[...Array(8)].map((_, idx) => (
              <div
                key={idx}
                className="flex-1 bg-gray-200 rounded-t animate-pulse"
                style={{
                  height: `${Math.random() * 70 + 10}%`,
                  animationDelay: `${idx * 100}ms`
                }}
              />
            ))}
          </div>
          {/* X axis skeleton */}
          <div className="absolute bottom-0 left-0 right-0 h-6 flex justify-between px-4">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="h-3 bg-gray-200 rounded w-12 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!weeklyData || weeklyData.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <ChartBarIcon className="h-12 w-12 mb-2" />
          <p className="text-sm">No issues data available</p>
        </div>
      </div>
    );
  }

  const maxIssues = Math.max(...weeklyData.map(d => d.issues));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Issues Per Week</h3>
          <p className="text-sm text-gray-600 mt-1">Last {days} days • Track issue frequency over time</p>
        </div>
        <div className="p-2 rounded-lg bg-red-100">
          <ChartBarIcon className="h-6 w-6 text-red-600" />
        </div>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={weeklyData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis
              dataKey="label"
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="issues" radius={[4, 4, 0, 0]}>
              {weeklyData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.issues > maxIssues * 0.7 ? '#EF4444' : entry.issues > maxIssues * 0.4 ? '#F59E0B' : '#3B82F6'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div>
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">This Week</p>
          <p className="text-2xl font-bold text-gray-900">
            {weeklyData[weeklyData.length - 1]?.issues || 0}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Avg/Week</p>
          <p className="text-2xl font-bold text-gray-900">
            {Math.round(weeklyData.reduce((sum, d) => sum + d.issues, 0) / weeklyData.length) || 0}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Peak Week</p>
          <p className="text-2xl font-bold text-gray-900">
            {maxIssues}
          </p>
        </div>
      </div>
    </div>
  );
};

export default IssuesTrendChart;
